import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	HostBinding,
	Input,
	NgZone,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import {
	RseObjectFit,
	RsePlayerState,
	RseWhepSession,
} from 'ramsys-stream-player-core';

/**
 * Angular 13 wrapper around ramsys-stream-player-core (same inputs as eye's <rse-player>).
 * The published Angular package requires Angular 18+.
 *
 * Retries when WHEP is not ready yet (stream id exists but watch path 404s).
 */
@Component({
	selector: 'rse-player',
	template: `
		<video
			#video
			autoplay
			playsinline
			[muted]="muted"
			[attr.controls]="controls ? '' : null"
		></video>
	`,
	styles: [`
		:host {
			display: block;
			width: 100%;
			height: 100%;
			background: #000;
			overflow: hidden;
		}
		video {
			display: block;
			width: 100%;
			height: 100%;
			background: #000;
			object-fit: var(--rse-object-fit, fill);
		}
	`]
})
export class RsePlayerComponent implements AfterViewInit, OnChanges, OnDestroy {
	private static readonly MAX_WHEP_RETRIES = 20;
	private static readonly WHEP_RETRY_MS = 1500;

	@Input() serverUrl = '';
	@Input() url = '';
	@Input() appToken = '';
	@Input() sessionToken = '';
	@Input() muted = true;
	@Input() controls = false;
	@Input() objectFit: RseObjectFit = 'fill';
	@Input() iceServers: RTCIceServer[] = [];
	@Input() iceGatherTimeoutMs = 2000;

	@Output() stateChange = new EventEmitter<RsePlayerState>();
	@Output() playbackError = new EventEmitter<Error>();

	@ViewChild('video', { static: true }) videoRef: ElementRef<HTMLVideoElement>;

	@HostBinding('style.--rse-object-fit')
	get objectFitStyle(): string {
		return this.objectFit;
	}

	private session: RseWhepSession | null = null;
	private viewReady = false;
	private lastKey = '';
	private whepRetryAttempt = 0;
	private whepRetryTimer: ReturnType<typeof setTimeout> | null = null;
	private destroyed = false;
	private readonly onPageHide = () => {
		void this.session?.stop();
	};

	constructor(private readonly zone: NgZone) { }

	ngAfterViewInit(): void {
		this.viewReady = true;
		window.addEventListener('pagehide', this.onPageHide);
		this.open();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.viewReady) {
			return;
		}
		if (
			changes['serverUrl'] ||
			changes['url'] ||
			changes['appToken'] ||
			changes['sessionToken'] ||
			changes['iceServers'] ||
			changes['iceGatherTimeoutMs']
		) {
			this.whepRetryAttempt = 0;
			this.clearWhepRetry();
			this.open();
		}
	}

	ngOnDestroy(): void {
		this.destroyed = true;
		this.clearWhepRetry();
		window.removeEventListener('pagehide', this.onPageHide);
		void this.session?.stop();
		this.session = null;
	}

	reload(): void {
		this.whepRetryAttempt = 0;
		this.clearWhepRetry();
		this.lastKey = '';
		this.open();
	}

	private open(): void {
		const video = this.videoRef?.nativeElement;
		if (!video) {
			return;
		}
		const key = JSON.stringify({
			serverUrl: this.serverUrl,
			url: this.url,
			appToken: this.appToken,
			sessionToken: this.sessionToken,
			iceServers: this.iceServers,
			iceGatherTimeoutMs: this.iceGatherTimeoutMs,
			whepRetryAttempt: this.whepRetryAttempt,
		});
		if (key === this.lastKey) {
			return;
		}
		this.lastKey = key;
		if (!this.session) {
			this.session = new RseWhepSession(
				(state) => this.zone.run(() => {
					if (state === 'playing') {
						this.whepRetryAttempt = 0;
						this.clearWhepRetry();
					}
					this.stateChange.emit(state);
				}),
				(error) => this.zone.run(() => this.onPlaybackError(error)),
			);
		}
		void this.session.start(video, {
			serverUrl: this.serverUrl,
			url: this.url,
			appToken: this.appToken,
			sessionToken: this.sessionToken,
			iceServers: this.iceServers,
			iceGatherTimeoutMs: this.iceGatherTimeoutMs,
		});
	}

	private onPlaybackError(error: Error): void {
		if (this.destroyed) {
			return;
		}
		if (this.isRetryableWhepError(error)
			&& this.whepRetryAttempt < RsePlayerComponent.MAX_WHEP_RETRIES) {
			this.whepRetryAttempt += 1;
			this.clearWhepRetry();
			this.whepRetryTimer = setTimeout(() => {
				this.whepRetryTimer = null;
				if (this.destroyed) {
					return;
				}
				this.lastKey = '';
				this.open();
			}, RsePlayerComponent.WHEP_RETRY_MS);
			return;
		}
		this.whepRetryAttempt = 0;
		this.playbackError.emit(error);
	}

	private isRetryableWhepError(error: Error): boolean {
		const message = (error && error.message) ? String(error.message) : '';
		return /WHEP request failed \((404|409|503)\)/i.test(message)
			|| /no stream is available/i.test(message)
			|| /stream not found/i.test(message);
	}

	private clearWhepRetry(): void {
		if (this.whepRetryTimer != null) {
			clearTimeout(this.whepRetryTimer);
			this.whepRetryTimer = null;
		}
	}
}
